// MyTrainees.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function TrainingStageBadge({ stage }) {
  if (!stage || stage === 'none') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-100 text-gray-600 border border-gray-200">
        No training
      </span>
    );
  }

  let text = stage;
  let cls =
    'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border font-semibold ';
  if (stage === 'shadow') {
    text = 'Shadow stage';
    cls += 'bg-amber-50 text-amber-800 border-amber-300';
  } else if (stage === 'supervised') {
    text = 'Supervised stage';
    cls += 'bg-emerald-50 text-emerald-800 border-emerald-300';
  } else {
    cls += 'bg-sky-50 text-sky-800 border-sky-300';
  }

  return <span className={cls}>{text}</span>;
}

function formatDateTime(dt) {
  if (!dt) return '—';
  const s = String(dt);
  if (s.includes('T')) {
    return s.slice(0, 16).replace('T', ' ');
  }
  return s.slice(0, 16);
}

function formatLevel(level) {
  if (!level) return '';
  if (level === 'needs_support') return 'Needs support';
  if (level === 'improving') return 'Improving';
  if (level === 'almost_independent') return 'Almost independent';
  if (level === 'ready_for_independent') return 'Ready for independent work';
  return level;
}

function formatPromotion(code) {
  if (!code || code === 'none') return 'No promotion suggestion';
  if (code === 'to_supervised') return 'Promote to supervised stage';
  if (code === 'to_independent') return 'Promote to independent work';
  return code;
}

export default function MyTrainees() {
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
  const currentTeacherId = currentUser?.id;

  const [loading, setLoading] = useState(true);
  const [allTrainees, setAllTrainees] = useState([]);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all'); // all | shadow | supervised

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyTrainee, setHistoryTrainee] = useState(null);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // ✨ novi BE endpoint
        const res = await API.get('/training/my-trainees');
        if (!isMounted) return;
        setAllTrainees(res.data || []);
      } catch (e) {
        console.error(e);
        if (!isMounted) return;
        setError('Failed to load trainees.');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const myTrainees = useMemo(() => {
    let list = allTrainees || [];

    if (stageFilter !== 'all') {
      list = list.filter(
        t =>
          (t.training_stage || '').toLowerCase() ===
          stageFilter.toLowerCase()
      );
    }

    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(t =>
        (t.trainee_name || '').toLowerCase().includes(s)
      );
    }

    return list;
  }, [allTrainees, search, stageFilter]);

  const openHistory = trainee => {
    if (!trainee?.trainee_id) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryRows([]);
    setHistoryTrainee(trainee);

    (async () => {
      try {
        const res = await API.get(
          `/training/trainee/${trainee.trainee_id}/history`
        );
        let rows = res.data || [];
        // ✨ sigurnosni filter: mentor vidi pre svega svoje zapise
        if (currentTeacherId) {
          rows = rows.filter(
            r =>
              !r.mentor_id ||
              Number(r.mentor_id) === Number(currentTeacherId)
          );
        }
        setHistoryRows(rows);
      } catch (e) {
        console.error(e);
        setHistoryRows([]);
      } finally {
        setHistoryLoading(false);
      }
    })();
  };

  const closeHistory = () => {
    setHistoryOpen(false);
    setHistoryRows([]);
    setHistoryTrainee(null);
  };

  const parseLastSuggest = last_suggest_raw => {
    if (!last_suggest_raw) return null;
    const [code, ts] = String(last_suggest_raw).split('|');
    return {
      code,
      date: ts || null,
      label: formatPromotion(code),
    };
  };

  return (
    <div className="p-3 max-w-5xl mx-auto">
      <h2 className="text-lg font-bold mb-2">My trainees</h2>

      <p className="text-sm text-gray-700 mb-4">
        Here you see all teachers in a <b>training stage</b> for whom you are
        configured as a mentor. From here you can review their{' '}
        <b>training visit feedback history</b> and see how close they are to
        independent work. Training visit planning and stage changes are handled
        by the <b>organization admin</b>.
      </p>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end mb-4">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Search by trainee name
          </label>
          <input
            type="text"
            className="w-full border rounded px-3 py-1.5 text-sm"
            placeholder="Start typing trainee name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="w-full sm:w-52">
          <label className="block text-xs font-semibold text-gray-600 mb-1">
            Training stage
          </label>
          <select
            className="w-full border rounded px-3 py-1.5 text-sm"
            value={stageFilter}
            onChange={e => setStageFilter(e.target.value)}
          >
            <option value="all">All stages</option>
            <option value="shadow">Shadow</option>
            <option value="supervised">Supervised</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-gray-400 text-sm">
          Loading trainees...
        </div>
      ) : !myTrainees.length ? (
        <div className="p-6 text-center text-gray-500 text-sm bg-white border rounded">
          You are currently not configured as a mentor for any trainees.
          <br />
          <span className="text-xs text-gray-400">
            (Organization admin can assign trainees to you in the teacher
            settings.)
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {myTrainees.map(t => {
            const lastSuggest = parseLastSuggest(t.last_suggest_raw);
            const total = Number(t.total_visits || 0);
            const reviewed = Number(t.reviewed_visits || 0);

            return (
              <div
                key={t.trainee_id}
                className="bg-white border rounded-xl shadow-sm p-3 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div>
                      <div className="font-semibold text-sm text-gray-900">
                        {t.trainee_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Teacher ID: {t.trainee_id}
                      </div>
                    </div>
                    <TrainingStageBadge stage={t.training_stage} />
                  </div>

                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <p>
                      Training visits with mentor feedback:{' '}
                      <b>
                        {reviewed} / {total}
                      </b>
                    </p>
                    <p>
                      Last training visit:{' '}
                      <b>{formatDateTime(t.last_visit_at)}</b>
                    </p>
                    {lastSuggest && (
                      <p>
                        Last promotion suggestion:{' '}
                        <b>{lastSuggest.label}</b>
                        {lastSuggest.date && (
                          <span className="text-[11px] text-gray-500">
                            {' '}
                            ({formatDateTime(lastSuggest.date)})
                          </span>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="mt-2 text-xs text-gray-600">
                    <p>
                      In <b>{t.training_stage || 'no'}</b> training stage.
                      You provide mentor feedback directly from{' '}
                      <b>My Visits Today</b> when this trainee joins your
                      visits. Here you can later review the full history of
                      training visits and your evaluations.
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => openHistory(t)}
                    className="px-3 py-1.5 text-xs font-semibold rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    View feedback history
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* HISTORY MODAL */}
      {historyOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Training history – {historyTrainee?.trainee_name || ''}
              </h3>
              <button
                className="text-gray-500"
                onClick={closeHistory}
              >
                ✕
              </button>
            </div>

            {historyLoading ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                Loading history...
              </div>
            ) : !historyRows.length ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No mentor feedback records found for this trainee.
              </div>
            ) : (
              <div className="space-y-3">
                {historyRows.map((row, idx) => (
                  <div
                    key={row.id || idx}
                    className="border rounded p-3 bg-gray-50"
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs text-gray-700">
                        <div className="font-semibold text-gray-900">
                          {row.day} {row.from_hhmm}–{row.to_hhmm}
                        </div>
                        <div>
                          Student:{' '}
                          <b>
                            {(row.student_last_name || '') +
                              (row.student_last_name ? ', ' : '') +
                              (row.student_first_name || '')}
                          </b>
                          {row.service_type && (
                            <span className="ml-2 text-[11px] uppercase text-gray-500">
                              [{row.service_type}]
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>
                          Mentor:{' '}
                          <span className="font-semibold text-gray-800">
                            {row.mentor_name || '—'}
                          </span>
                        </div>
                        <div>
                          Stage at visit:{' '}
                          <span>{row.stage_at_visit || '—'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 text-xs mb-2">
                      {row.mentor_rating != null && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          Rating: {row.mentor_rating}/5
                        </span>
                      )}
                      {row.mentor_level && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          Level: {formatLevel(row.mentor_level)}
                        </span>
                      )}
                      {row.suggest_promotion &&
                        row.suggest_promotion !== 'none' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                            Promotion: {formatPromotion(row.suggest_promotion)}
                          </span>
                        )}
                      {row.is_flagged ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          Flagged for admin review
                        </span>
                      ) : null}
                    </div>

                    {row.mentor_comment && (
                      <div className="text-xs text-gray-700 whitespace-pre-line">
                        {row.mentor_comment}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

